// Strategies tab — list every strategy owned by this API key as a card grid with
// search + status filter. Auto-refreshes every 5s so status changes show up quickly.

import { useMemo, useState } from "react";
import { Plus, Search, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useStrategies } from "../../hooks/useStrategies";
import { useNavigation } from "../../state/NavigationContext";
import { StrategyCard } from "./StrategyCard";
import { cn } from "@/lib/utils";

type Filter = "all" | "active" | "paused" | "draft";

const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "paused", label: "Paused" },
  { id: "draft", label: "Drafts" },
];

export function StrategiesPage() {
  const { strategies, loading, error, reload } = useStrategies(5000);
  const { navigateTo } = useNavigation();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return strategies.filter((s) => {
      if (filter !== "all" && s.status !== filter) return false;
      if (!q) return true;
      const haystack = `${s.name} ${s.spec.coin} ${s.spec.interval} ${s.spec.marketType}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [strategies, search, filter]);

  const Toolbar = (
    <Card className="p-3 bg-card-elevated flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2 flex-1 min-w-[260px]">
        <div className="relative flex-1 max-w-[420px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, coin, interval…"
            className="pl-8 h-9"
          />
        </div>
        <div className="flex items-center gap-1 bg-card rounded-md p-1 border">
          {FILTERS.map((f) => {
            const active = f.id === filter;
            const count = f.id === "all"
              ? strategies.length
              : strategies.filter((s) => s.status === f.id).length;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={cn(
                  "px-2.5 py-1 text-xs rounded font-medium transition-colors flex items-center gap-1.5",
                  active
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {f.label}
                <span className={cn(
                  "text-[10px] font-mono px-1.5 py-0.5 rounded",
                  active ? "bg-primary-foreground/20" : "bg-secondary",
                )}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <Button
        onClick={() => navigateTo("builder")}
        className="bg-primary hover:bg-primary/90 text-primary-foreground"
      >
        <Plus className="size-4" />
        New strategy
      </Button>
    </Card>
  );

  if (loading && strategies.length === 0) {
    return (
      <div className="space-y-3">
        {Toolbar}
        <div className="grid gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-[120px]" />
          ))}
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="space-y-3">
        {Toolbar}
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Couldn't load strategies</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }
  if (strategies.length === 0) {
    return (
      <div className="space-y-3">
        {Toolbar}
        <Card className="p-10 text-center bg-card-elevated">
          <div className="text-5xl mb-3">🤖</div>
          <h2 className="text-lg font-semibold tracking-tight mb-2">No strategies yet</h2>
          <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4 leading-relaxed">
            Strategies are saved bots that watch the market for you. Build one in the Builder —
            pick an indicator, tick the entry conditions, choose where alerts get delivered, and
            hit Save + Activate.
          </p>
          <Button
            onClick={() => navigateTo("builder")}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <Plus className="size-4" />
            Create your first strategy
          </Button>
        </Card>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {Toolbar}
      {filtered.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground bg-card-elevated">
          No strategies match the current search/filter.
        </Card>
      ) : (
        <div className="grid gap-3">
          {filtered.map((s) => (
            <StrategyCard key={s.id} strategy={s} onChanged={reload} />
          ))}
        </div>
      )}
    </div>
  );
}
